import { useState, useEffect, useCallback, useRef } from 'react';
import { Quote, QuoteLineItem } from '@/types/quote';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Document, Page, pdfjs } from 'react-pdf';
import { useQuotePDF } from '@/hooks/useQuotePDF';
import { QuotePreviewThumbnails } from './QuotePreviewThumbnails';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface QuoteSplitPreviewProps {
  quote: Quote;
  lineItems: QuoteLineItem[];
  className?: string;
}

export const QuoteSplitPreview = ({ quote, lineItems, className }: QuoteSplitPreviewProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(0.65);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { generatePDFBytes } = useQuotePDF();
  
  // Debounce ref for auto-refresh
  const refreshTimeoutRef = useRef<NodeJS.Timeout>();
  const lastDataRef = useRef<string>('');

  // Generate PDF preview
  const generatePreview = useCallback(async () => {
    if (!quote.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const pdfBytes = await generatePDFBytes(quote.id);
      if (pdfBytes) {
        // Create blob directly from Uint8Array using spread to avoid TypeScript buffer issues
        const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        // Revoke old URL
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
        }
        
        setPdfUrl(url);
      } else {
        setError('Kon PDF niet genereren');
      }
    } catch (err: any) {
      console.error('Preview generation error:', err);
      setError(err.message || 'Fout bij genereren preview');
    } finally {
      setLoading(false);
    }
  }, [quote.id, generatePDFBytes, pdfUrl]);

  // Auto-refresh when data changes (debounced). The timer is only cleared by a newer
  // change or on unmount: clearing it on every re-render (a save status, a refetch)
  // dropped the refresh while the data hash stayed the same.
  const generateRef = useRef(generatePreview);
  generateRef.current = generatePreview;
  useEffect(() => {
    const dataHash = JSON.stringify({
      quote_number: quote.quote_number,
      title: quote.title,
      organization_id: quote.organization_id,
      contact_id: quote.contact_id,
      quote_date: quote.quote_date,
      valid_until: quote.valid_until,
      pricing_mode: quote.pricing_mode,
      project_goal: quote.project_goal,
      work_description: quote.work_description,
      advice_conclusion: quote.advice_conclusion,
      disclaimer: quote.disclaimer,
      additional_conditions: quote.additional_conditions,
      payment_conditions: quote.payment_conditions,
      terms: quote.terms,
      template_id: quote.template_id,
      lineItems: lineItems.map((i) => [i.position, i.description, i.quantity, i.unit_price, i.vat_rate]),
    });

    // Skip if data hasn't changed; the first hash is the initial load below.
    if (dataHash === lastDataRef.current) return;
    const first = lastDataRef.current === '';
    lastDataRef.current = dataHash;
    if (first) return;

    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    // Debounced refresh (1.5s after last change)
    refreshTimeoutRef.current = setTimeout(() => generateRef.current(), 1500);
  }, [quote, lineItems]);

  useEffect(() => () => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
  }, []);

  // Initial load
  useEffect(() => {
    generatePreview();
    
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [quote.id]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('[QuoteSplitPreview] PDF loaded with', numPages, 'pages');
    setNumPages(numPages);
    // Behoud huidige pagina indien geldig, anders reset naar 1
    setCurrentPage(prev => (prev <= numPages ? prev : 1));
  };

  const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, numPages));


  return (
    <div className={cn("border rounded-lg bg-card flex flex-col", className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">Live Preview</h3>
          {loading && (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale(s => Math.max(s - 0.1, 0.3))}
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale(s => Math.min(s + 0.1, 1.5))}
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={generatePreview}
            disabled={loading}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-auto p-4 flex justify-center bg-muted/20">
        {error ? (
          <div className="flex flex-col items-center justify-center text-center py-8">
            <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={generatePreview}>
              Opnieuw proberen
            </Button>
          </div>
        ) : !pdfUrl ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Preview laden...</p>
          </div>
        ) : (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            }
            error={
              <div className="text-center py-8">
                <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Kan PDF niet laden</p>
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg"
            />
          </Document>
        )}
      </div>

      {/* Footer - Page navigation */}
      {numPages > 0 && (
        <div className="border-t bg-muted/30 p-2 space-y-2">
          {/* Page thumbnails */}
          <QuotePreviewThumbnails 
            numPages={numPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
          
          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground">
              Pagina {currentPage} van {numPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage >= numPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Totals summary - conditioneel op pricing_mode */}
      <div className="border-t p-3 bg-card">
        <div className="space-y-1 text-sm">
          {quote.pricing_mode !== 'exclusive' && (
            <>
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotaal</span>
                <span>{formatCurrency(quote.subtotal_amount || 0)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>BTW</span>
                <span>{formatCurrency(quote.vat_amount || 0)}</span>
              </div>
            </>
          )}
          <div className={cn(
            "flex justify-between font-semibold",
            quote.pricing_mode !== 'exclusive' && "pt-1 border-t"
          )}>
            <span>{quote.pricing_mode === 'exclusive' ? 'Totaal excl. BTW' : 'Totaal'}</span>
            <span className="text-primary">{formatCurrency(quote.total_amount || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function for currency formatting
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
};
